import {
  forwardRef,
} from 'react'

import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components'

import {
  cva,
  type VariantProps,
} from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'cursor-pointer rounded-lg font-semibold outline-none',
    'transition-[background-color,border-color,color,box-shadow]',
    'duration-150',

    'data-pressed:shadow-none',

    'data-focus-visible:ring-2',
    'data-focus-visible:ring-[var(--color-primary)]',
    'data-focus-visible:ring-offset-2',
    'data-focus-visible:ring-offset-[var(--color-surface)]',

    'data-disabled:pointer-events-none',
    'data-disabled:cursor-not-allowed',
    'data-disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: [
          'border border-transparent',
          'bg-[var(--color-primary-solid)]',
          'text-[var(--color-primary-foreground)]',
          'shadow-[0_1px_2px_rgb(15_23_42/0.18)]',
          'data-hovered:bg-[var(--color-primary-solid-hover)]',
          'data-hovered:shadow-[0_3px_8px_-4px_rgb(15_23_42/0.45)]',
        ],

        secondary: [
          'border border-[var(--color-border)]',
          'bg-[var(--color-surface)]',
          'text-[var(--color-foreground)]',
          'data-hovered:bg-[var(--color-surface-muted)]',
          'data-hovered:border-[var(--color-border-strong)]',
        ],

        outline: [
          'border border-[var(--color-primary)]',
          'bg-transparent',
          'text-[var(--color-primary)]',
          'data-hovered:bg-[var(--color-primary-muted)]',
        ],

        danger: [
          'border border-transparent',
          'bg-[var(--color-danger)]',
          'text-white',
          'data-hovered:bg-[var(--color-danger-hover)]',
        ],

        outlineDanger: [
          'border border-[var(--color-danger)]',
          'bg-transparent',
          'text-[var(--color-danger)]',
          'data-hovered:bg-[var(--color-danger)]',
          'data-hovered:text-white',
        ],

        ghost: [
          'border border-transparent',
          'bg-transparent',
          'text-[var(--color-foreground)]',
          'data-hovered:bg-[var(--color-surface-muted)]',
        ],
      },

      size: {
        small: 'h-9 px-3 text-sm',
        medium: 'h-10 px-4 text-sm',
        large: 'h-12 px-6 text-base',
      },

      isIconOnly: {
        true: 'aspect-square px-0',
        false: '',
      },
    },

    compoundVariants: [
      {
        size: 'small',
        isIconOnly: true,
        className: 'w-9',
      },
      {
        size: 'medium',
        isIconOnly: true,
        className: 'w-10',
      },
      {
        size: 'large',
        isIconOnly: true,
        className: 'w-12',
      },
    ],

    defaultVariants: {
      variant: 'primary',
      size: 'medium',
      isIconOnly: false,
    },
  },
)

export type ButtonProps =
  Omit<
    AriaButtonProps,
    'className'
  > &
  VariantProps<
    typeof buttonVariants
  > & {
    className?: string
  }

export const Button =
  forwardRef<
    HTMLButtonElement,
    ButtonProps
  >(function Button(
    {
      variant,
      size,
      isIconOnly,
      className,
      ...props
    },
    ref,
  ) {
    return (
      <AriaButton
        ref={ref}
        {...props}
        className={cn(
          buttonVariants({
            variant,
            size,
            isIconOnly,
          }),
          className,
        )}
      />
    )
  })
